import React, { useEffect, useState, useRef, useCallback } from "react";
import { Loader2, IndianRupee, Download, CalendarClock, CheckCircle2, Receipt, Mail, Upload, AlertTriangle, CalendarPlus, Image as ImageIcon, Plus, X, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api, formatDate } from "@/lib/api";
import { downloadReceiptPdf, emailReceipt } from "@/lib/receipt";
import { ExplanationField } from "@/components/ExplanationField";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const stBadge = (s) => ({ paid: "bg-green-600", pending: "bg-amber-500", overdue: "bg-red-600", approved: "bg-green-600", rejected: "bg-red-600", pending_clearance: "bg-amber-500", waived: "bg-slate-400" }[s] || "bg-slate-500");
const stLabel = (s) => ({ paid: "Paid", pending: "Pending", overdue: "Overdue", approved: "Approved", rejected: "Rejected", pending_clearance: "Cheque pending", waived: "Waived" }[s] || s);

const ALERT_STYLE = {
  paid: "border-green-300 bg-green-50 text-green-800",
  overdue: "border-red-300 bg-red-50 text-red-800",
  due_today: "border-red-300 bg-red-50 text-red-800",
  due_soon: "border-amber-300 bg-amber-50 text-amber-800",
  upcoming: "border-blue-200 bg-blue-50 text-blue-800",
  pending: "border-amber-300 bg-amber-50 text-amber-800",
};

function PayForm({ outstanding, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ amount: "", mode: "upi", reference: "", paidAt: "", paperReceiptNumber: "", proof: "", proofName: "" });
  const [token, setToken] = useState(() => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random())));
  const [saving, setSaving] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scan, setScan] = useState(null);
  const proofRef = useRef(null);
  const extraRef = useRef(null);
  const [extras, setExtras] = useState([]);
  const addExtra = (e) => {
    const fl = e.target.files?.[0]; if (!fl) return;
    const r = new FileReader(); r.onload = () => {
      const dataUrl = r.result;
      setExtras((a) => [...a, { proof: dataUrl, name: fl.name, mode: form.mode, amount: "" }]);
      api.post("/fees/me/proof/extract", { proof: dataUrl, mode: form.mode }).catch(() => {});
    }; r.readAsDataURL(fl);
    if (extraRef.current) extraRef.current.value = "";
  };

  useEffect(() => { if (open && !form.amount) setForm((x) => ({ ...x, amount: String(outstanding || "") })); }, [open]); // eslint-disable-line

  const runScan = async (dataUrl, mode) => {
    if (!dataUrl) return;
    setScanning(true); setScan(null);
    try {
      const amt = parseInt(form.amount, 10) || 0;
      const { data } = await api.post("/fees/me/proof/extract", { proof: dataUrl, mode, amount: amt });
      setScan(data);
      const f = data.fields || {};
      setForm((x) => {
        const nx = { ...x };
        if (mode !== "cash") { if (f.reference?.value) nx.reference = String(f.reference.value); if (f.date?.combined) nx.paidAt = f.date.combined; }
        else if (f.paper_receipt_number?.value) nx.paperReceiptNumber = String(f.paper_receipt_number.value);
        if (f.amount?.value && !nx.amount) nx.amount = String(f.amount.value);
        return nx;
      });
    } catch { toast.error("Couldn't read the photo automatically. Please enter details manually."); }
    finally { setScanning(false); }
  };
  const onProof = (e) => {
    const fl = e.target.files?.[0]; if (!fl) return;
    if (fl.size > 9_000_000) { toast.error("Photo too large (max ~9MB)."); return; }
    const r = new FileReader(); r.onload = () => { setForm((x) => ({ ...x, proof: r.result, proofName: fl.name })); runScan(r.result, form.mode); }; r.readAsDataURL(fl);
  };
  const removeProof = () => { setForm((x) => ({ ...x, proof: "", proofName: "" })); setScan(null); if (proofRef.current) proofRef.current.value = ""; };

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseInt(form.amount, 10) || 0;
    if (amt <= 0) { toast.error("Enter the amount you paid"); return; }
    setSaving(true);
    try {
      const proofs = [
        ...(form.proof ? [{ proof: form.proof, mode: form.mode, amount: null }] : []),
        ...extras.map((x) => ({ proof: x.proof, mode: x.mode || form.mode, amount: x.amount ? Number(x.amount) : null })),
      ];
      const { data } = await api.post("/fees/me/collections", {
        amount: amt, mode: form.mode, reference: form.reference || undefined,
        paper_receipt_number: form.paperReceiptNumber || undefined,
        proof: form.proof || undefined, proofs: proofs.length ? proofs : undefined,
        paid_at: form.paidAt || undefined, explanation: (form.explanation || "").trim() || undefined, client_token: token,
      });
      toast.success(data.message || "Submitted for approval");
      setForm({ amount: "", mode: "upi", reference: "", paidAt: "", paperReceiptNumber: "", proof: "", proofName: "" });
      setExtras([]); setScan(null); setOpen(false);
      setToken(crypto.randomUUID ? crypto.randomUUID() : String(Date.now() + Math.random()));
      if (proofRef.current) proofRef.current.value = "";
      onDone && onDone();
    } catch (err) { toast.error(err.response?.data?.detail || "Submission failed"); }
    finally { setSaving(false); }
  };

  if (!open) return (
    <Button onClick={() => setOpen(true)} className="w-full h-12 bg-green-600 hover:bg-green-700" data-testid="pay-open-btn"><Upload className="h-5 w-5 mr-1.5" /> I've paid — upload proof</Button>
  );

  return (
    <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid="pay-form">
      <div className="flex items-center justify-between"><h4 className="font-bold text-slate-900">Submit a payment proof</h4><Button type="button" size="sm" variant="ghost" onClick={() => setOpen(false)} data-testid="pay-close"><X className="h-4 w-4" /></Button></div>
      <p className="text-[11px] text-slate-500">Your payment stays <b>pending admin approval</b> — the balance updates and the receipt is issued only after PTG approves.</p>
      <div className="grid grid-cols-2 gap-3">
        <div><Label className="text-xs">Amount paid (₹)</Label><Input type="number" inputMode="numeric" value={form.amount} onChange={(e) => setForm((x) => ({ ...x, amount: e.target.value }))} data-testid="pay-amount" /></div>
        <div><Label className="text-xs">Method</Label>
          <div className="grid grid-cols-3 gap-1 mt-1">
            {["upi", "cheque", "cash"].map((m) => (
              <Button key={m} type="button" size="sm" variant={form.mode === m ? "default" : "outline"} className={form.mode === m ? "bg-blue-900" : ""} onClick={() => { setForm((x) => ({ ...x, mode: m })); if (form.proof) runScan(form.proof, m); }} data-testid={`pay-mode-${m}`}>{m.toUpperCase()}</Button>
            ))}
          </div>
        </div>
      </div>
      {form.mode !== "cash" && <div><Label className="text-xs">Reference / UTR / Cheque no.</Label><Input value={form.reference} onChange={(e) => setForm((x) => ({ ...x, reference: e.target.value }))} data-testid="pay-reference" /></div>}
      {form.mode !== "cash" && <div><Label className="text-xs">Payment date &amp; time (optional)</Label><Input type="datetime-local" value={form.paidAt} onChange={(e) => setForm((x) => ({ ...x, paidAt: e.target.value }))} data-testid="pay-paidat" /></div>}
      {form.mode === "cash" && <div><Label className="text-xs">Paper receipt number (optional)</Label><Input value={form.paperReceiptNumber} onChange={(e) => setForm((x) => ({ ...x, paperReceiptNumber: e.target.value }))} data-testid="pay-paper-receipt" /></div>}
      <div>
        <Label className="text-xs">Payment proof photo (optional)</Label>
        <input ref={proofRef} type="file" accept="image/*" onChange={onProof} className="hidden" data-testid="pay-proof" />
        {!form.proof ? (
          <Button type="button" variant="outline" className="mt-1 w-full h-11" onClick={() => proofRef.current?.click()} data-testid="pay-proof-choose"><ImageIcon className="h-4 w-4 mr-1.5" /> Choose photo / take a picture</Button>
        ) : (
          <div className="mt-1 flex items-center gap-3 rounded-xl border border-slate-200 p-2" data-testid="pay-proof-preview">
            <img src={form.proof} alt="proof" className="h-14 w-14 rounded-lg object-cover border" />
            <div className="min-w-0 flex-1"><p className="text-xs font-medium text-slate-700 truncate">{form.proofName}</p></div>
            <Button type="button" size="sm" variant="ghost" className="text-red-600" onClick={removeProof} data-testid="pay-proof-remove">Remove</Button>
          </div>
        )}
      </div>
      <div data-testid="pay-extra-proofs">
        <Label className="text-xs">More proofs (optional) — for part payments or cash + bank</Label>
        <input ref={extraRef} type="file" accept="image/*" onChange={addExtra} className="hidden" data-testid="pay-extra-input" />
        {extras.map((x, idx) => (
          <div key={idx} className="mt-1 flex items-center gap-2 rounded-xl border border-slate-200 p-2" data-testid={`pay-extra-${idx}`}>
            <img src={x.proof} alt="extra proof" className="h-12 w-12 rounded-lg object-cover border shrink-0" />
            <select value={x.mode} onChange={(e) => setExtras((a) => a.map((it, i) => i === idx ? { ...it, mode: e.target.value } : it))} className="text-xs border rounded px-1 py-1" data-testid={`pay-extra-mode-${idx}`}>
              <option value="upi">UPI</option><option value="cheque">CHEQUE</option><option value="cash">CASH</option><option value="bank">BANK</option>
            </select>
            <Input type="number" placeholder="₹ part" value={x.amount} onChange={(e) => setExtras((a) => a.map((it, i) => i === idx ? { ...it, amount: e.target.value } : it))} className="h-8 w-20 text-xs" data-testid={`pay-extra-amount-${idx}`} />
            <Button type="button" size="sm" variant="ghost" className="text-red-600 shrink-0 px-2" onClick={() => setExtras((a) => a.filter((_, i) => i !== idx))} data-testid={`pay-extra-remove-${idx}`}>×</Button>
          </div>
        ))}
        <Button type="button" variant="outline" className="mt-1 w-full h-9" onClick={() => extraRef.current?.click()} data-testid="pay-extra-add"><ImageIcon className="h-4 w-4 mr-1.5" /> Add another proof image</Button>
      </div>
      {form.proof && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="pay-scan">
          {scanning ? <p className="text-xs text-slate-600 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Reading the photo…</p> : scan ? (<>
            <p className="text-xs font-semibold text-slate-700">Read from photo {scan.cached && <span className="text-slate-400 font-normal">(reused)</span>}</p>
            {scan.duplicate && <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded-lg p-2" data-testid="pay-duplicate">Possible duplicate: reference <b>{scan.duplicate.reference}</b> already appears on a payment. Please check before submitting.</div>}
            {Object.entries(scan.fields || {}).map(([k, f]) => {
              const cls = f.status === "ok" ? "bg-green-100 text-green-700" : f.status === "review" ? "bg-amber-100 text-amber-700" : "bg-slate-200 text-slate-500";
              const tag = f.status === "ok" ? "read" : f.status === "review" ? "review" : "not found";
              const label = { amount: "Amount", date: "Date/time", reference: "Reference", paper_receipt_number: "Paper receipt" }[k] || k;
              return <div key={k} className="flex items-start justify-between gap-2 text-xs" data-testid={`pay-field-${k}`}><div className="min-w-0"><span className="text-slate-500">{label}: </span><span className="font-medium text-slate-800">{f.value ?? "—"}</span>{f.note && <p className="text-[11px] text-slate-400">{f.note}</p>}</div><Badge className={`${cls} shrink-0`}>{tag}</Badge></div>;
            })}
            <p className="text-[11px] text-slate-400">Please check these before submitting — you can edit any field above.</p>
          </>) : null}
        </div>
      )}
      <ExplanationField value={form.explanation || ""} onChange={(v) => setForm((x) => ({ ...x, explanation: v }))} testidPrefix="pay-explanation" />
      <Button type="submit" disabled={saving} className="w-full h-11 bg-green-600 hover:bg-green-700" data-testid="pay-submit">{saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Submit for approval"}</Button>
    </form>
  );
}

const PLAN_BADGE = { paid: ["Paid", "bg-green-100 text-green-700"], overdue: ["Overdue", "bg-red-100 text-red-700"], due: ["Due today", "bg-amber-100 text-amber-700"], upcoming: ["Upcoming", "bg-slate-100 text-slate-600"] };

function PlanRows({ cp }) {
  return (
    <div className="space-y-2" data-testid="fees-plan-rows">
      {cp.collect_now > 0 && <div className={`rounded-xl p-3 text-sm font-semibold ${cp.overdue ? "bg-red-50 text-red-700 border border-red-200" : "bg-amber-50 text-amber-800 border border-amber-200"}`} data-testid="fees-plan-now">Pay now: {inr(cp.collect_now)}{cp.overdue ? " (overdue)" : ""}</div>}
      {cp.rows.map((r, i) => {
        const [label, cls] = PLAN_BADGE[r.status] || PLAN_BADGE.upcoming;
        return (
          <div key={i} className="rounded-xl border border-slate-200 bg-white p-3 flex items-center justify-between gap-2" data-testid={`fees-plan-row-${i}`}>
            <div className="min-w-0"><p className="font-semibold text-slate-800">{inr(r.amount)}</p><p className="text-xs text-slate-500">by {r.date ? formatDate(r.date) : "—"}</p>{r.paid > 0 && r.remaining > 0 && <p className="text-[11px] text-amber-700">Part-paid {inr(r.paid)} · {inr(r.remaining)} left</p>}</div>
            <Badge className={`${cls} shrink-0`}>{label}</Badge>
          </div>
        );
      })}
      {cp.next && <p className="text-xs text-slate-500" data-testid="fees-plan-next">Next: {inr(cp.next.amount)}{cp.next.date ? ` by ${formatDate(cp.next.date)}` : ""}</p>}
      {cp.unscheduled > 0 && <p className="text-xs text-amber-600">Not yet scheduled: {inr(cp.unscheduled)}</p>}
    </div>
  );
}

function ProposeSchedule({ d, onDone }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const monthly = d.plan_type === "monthly";
  const unpaid = d.unpaid_months || [];
  const [period, setPeriod] = useState("");
  const [date, setDate] = useState("");
  const outstanding = d.summary.outstanding;
  const cp = d.collect_plan;
  const [rows, setRows] = useState(() => (cp?.rows?.length ? cp.rows.map((r) => ({ amount: String(r.amount), date: r.date || "", locked: r.status === "paid" })) : [{ amount: "", date: "" }]));
  const [note, setNote] = useState("");
  const already = (cp?.rows || []).reduce((s, r) => s + (r.paid || 0), 0);
  const planned = Math.max(0, rows.reduce((s, r) => s + (parseInt(r.amount, 10) || 0), 0) - already);
  const setRow = (i, k, v) => setRows((a) => a.map((x, j) => (j === i ? { ...x, [k]: v } : x)));

  const submitMonthly = async () => {
    if (!period || !date) { toast.error("Pick a month and a date"); return; }
    setSaving(true);
    try { await api.post("/fees/me/schedule-proposals", { type: "monthly_date", monthly_period: period, proposed_date: date }); toast.success("Proposal sent to admin for review"); setOpen(false); onDone && onDone(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not submit"); } finally { setSaving(false); }
  };
  const submitLump = async () => {
    for (const r of rows) { if (!(parseInt(r.amount, 10) > 0) || !r.date) { toast.error("Each row needs an amount and a date"); return; } }
    if (planned > outstanding) { toast.error(`Unpaid rows can't be more than ${inr(outstanding)}`); return; }
    setSaving(true);
    try { await api.post("/fees/me/schedule-proposals", { type: "collect_plan", note, installments: rows.map((r) => ({ amount: parseInt(r.amount, 10), date: r.date })) }); toast.success("Request sent to admin for review"); setOpen(false); onDone && onDone(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not submit"); } finally { setSaving(false); }
  };

  const lp = d.latest_proposal;
  return (
    <section data-testid="propose-section">
      <h3 className="font-bold text-slate-900 flex items-center gap-2 mb-2"><CalendarPlus className="h-5 w-5" /> Payment schedule</h3>
      {lp && (
        <div className="rounded-xl border border-slate-200 bg-white p-3 mb-2 text-sm" data-testid="proposal-status">
          <div className="flex items-center justify-between"><span className="font-semibold">Your last proposal</span><Badge className={stBadge(lp.status)}>{lp.status === "pending" ? "Awaiting admin" : stLabel(lp.status)}</Badge></div>
          <p className="text-xs text-slate-500 mt-1">{lp.type === "monthly_date" ? `Monthly ${lp.monthly_period} → ${formatDate(lp.proposed_date)}` : `${(lp.installments || []).length} payment row(s) requested`}</p>
          {lp.admin_note && <p className="text-xs text-slate-600 mt-1">Admin note: {lp.admin_note}</p>}
          {lp.status === "approved" && lp.final && <p className="text-xs text-green-700 mt-1">Approved schedule applied — see above.</p>}
        </div>
      )}
      {d.summary.outstanding > 0 && (!lp || lp.status !== "pending") && (
        !open ? <Button variant="outline" onClick={() => setOpen(true)} data-testid="propose-open">{monthly ? "Propose a payment plan" : "Request change of amount or date"}</Button> : (
          <div className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3" data-testid="propose-form">
            <div className="flex items-center justify-between"><h4 className="font-semibold">{monthly ? "Propose an expected date" : "Request a change"}</h4><Button size="sm" variant="ghost" onClick={() => setOpen(false)}><X className="h-4 w-4" /></Button></div>
            <p className="text-[11px] text-slate-500">This is a request only. Admin will review and can adjust before approving. It does not change your balance.</p>
            {monthly ? (
              <div className="space-y-2">
                <Label className="text-xs">Month</Label>
                <select className="w-full border rounded-lg h-10 px-2 text-sm" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="propose-month">
                  <option value="">Select an unpaid month</option>
                  {unpaid.map((m) => <option key={m.period} value={m.period}>{m.label} · {inr(m.amount)}</option>)}
                </select>
                <Label className="text-xs">Proposed date</Label>
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="propose-date" />
                <Button onClick={submitMonthly} disabled={saving} className="w-full bg-blue-900" data-testid="propose-submit-monthly">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send proposal"}</Button>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-xs text-slate-500">Change the amounts or dates you'd like to pay. Paid rows can't be changed. Unpaid rows can't be more than {inr(outstanding)}.</p>
                {rows.map((it, i) => (
                  <div key={i} className="flex gap-2 items-end" data-testid={`propose-inst-${i}`}>
                    <div className="flex-1"><Label className="text-[11px]">Amount ₹</Label><Input type="number" inputMode="numeric" disabled={it.locked} value={it.amount} onChange={(e) => setRow(i, "amount", e.target.value)} data-testid={`propose-inst-amount-${i}`} /></div>
                    <div className="flex-1"><Label className="text-[11px]">Date</Label><Input type="date" disabled={it.locked} value={it.date} onChange={(e) => setRow(i, "date", e.target.value)} data-testid={`propose-inst-date-${i}`} /></div>
                    {!it.locked && rows.length > 1 && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => setRows((a) => a.filter((_, j) => j !== i))} data-testid={`propose-inst-remove-${i}`}><X className="h-4 w-4" /></Button>}
                  </div>
                ))}
                {rows.length < 6 && <Button size="sm" variant="outline" onClick={() => setRows((a) => [...a, { amount: "", date: "" }])} data-testid="propose-add-inst"><Plus className="h-4 w-4 mr-1" /> Add row</Button>}
                <p className={`text-xs font-medium ${planned > outstanding ? "text-red-600" : "text-slate-600"}`} data-testid="propose-total">Planned {inr(planned)} / {inr(outstanding)} due</p>
                <Input placeholder="Reason (optional)" value={note} onChange={(e) => setNote(e.target.value)} data-testid="propose-note" />
                <Button onClick={submitLump} disabled={saving} className="w-full bg-blue-900" data-testid="propose-submit-lump">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send request"}</Button>
              </div>
            )}
          </div>
        )
      )}
    </section>
  );
}

export default function MyFees() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    try { const { data } = await api.get("/fees/me"); setD(data); }
    catch { toast.error("Could not load your fees"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-blue-900" /></div>;
  if (!d) return <p className="text-sm text-slate-500">No fee data available.</p>;
  const sm = d.summary;
  const al = d.fee_alert;

  return (
    <div className="space-y-6 w-full max-w-full overflow-x-hidden" data-testid="my-fees">
      <div className="rounded-3xl bg-gradient-to-br from-blue-900 to-sky-700 text-white p-5 sm:p-6" data-testid="fees-summary-card">
        <p className="text-sky-200 text-xs uppercase tracking-widest">{d.student.student_class} · Roll {d.student.roll_number}</p>
        <p className="text-2xl font-extrabold break-words">{d.student.name}</p>
        <div className="mt-4 grid grid-cols-3 gap-2 sm:gap-3 text-center">
          <div className="rounded-xl bg-white/10 p-3"><p className="text-[11px] text-sky-200">Total Fee</p><p className="font-bold" data-testid="fees-total">{inr(sm.total_fee)}</p></div>
          <div className="rounded-xl bg-white/10 p-3"><p className="text-[11px] text-sky-200">Paid</p><p className="font-bold text-green-300" data-testid="fees-paid">{inr(sm.total_paid)}</p></div>
          <div className="rounded-xl bg-white/10 p-3"><p className="text-[11px] text-sky-200">Outstanding</p><p className="font-bold text-amber-200" data-testid="fees-outstanding">{inr(sm.outstanding)}</p></div>
        </div>
        <div className="mt-3 flex items-center justify-between flex-wrap gap-2 text-sm">
          <span className="text-sky-100">Next due: <b>{sm.next_due_date ? formatDate(sm.next_due_date) : "—"}</b></span>
          <Badge className={stBadge(sm.status)} data-testid="fees-status">{stLabel(sm.status)}</Badge>
        </div>
      </div>

      {al && (
        <div className={`rounded-2xl border p-4 font-semibold flex items-center gap-2 ${ALERT_STYLE[al.level] || "border-slate-200 bg-slate-50 text-slate-700"}`} data-testid="fees-alert">
          {al.level === "paid" ? <CheckCircle2 className="h-5 w-5" /> : <AlertTriangle className="h-5 w-5" />}
          <span data-testid="fees-alert-text">{al.message}</span>
        </div>
      )}
      {d.pending_submission && (
        <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 text-amber-800 flex items-center gap-2" data-testid="fees-pending-submission">
          <Clock className="h-5 w-5" /> A payment of {inr(d.pending_submission.amount)} is awaiting admin approval. Your balance updates once approved.
        </div>
      )}

      {sm.outstanding > 0 && <PayForm outstanding={sm.outstanding} onDone={load} />}

      {d.monthly && (
        <section data-testid="fees-monthly">
          <div className="flex items-center justify-between flex-wrap gap-1 mb-2">
            <h3 className="font-bold text-slate-900 flex items-center gap-2"><CalendarClock className="h-5 w-5" /> Monthly fees</h3>
            <span className="text-sm text-slate-600">Monthly fee: <b>{inr(d.monthly.monthly_fee)}</b>{d.monthly.pending_from && <> · Pending from <b className="text-amber-600">{d.monthly.pending_from}</b></>}</span>
          </div>
          <div className="space-y-2">
            {d.monthly.months.map((m) => (
              <div key={m.period} className="rounded-xl border border-slate-200 bg-white p-3 flex items-center justify-between gap-2" data-testid={`fees-month-${m.period}`}>
                <div className="min-w-0"><p className="font-semibold text-slate-800">{m.label}</p><p className="text-xs text-slate-500">Due {m.due_date ? formatDate(m.due_date) : "—"}</p></div>
                <div className="flex items-center gap-2 sm:gap-3 shrink-0"><span className="font-bold text-slate-900">{inr(m.amount)}</span><Badge className={stBadge(m.status)}>{stLabel(m.status)}</Badge></div>
              </div>
            ))}
          </div>
        </section>
      )}

      {d.lumpsum && (
        <section data-testid="fees-lumpsum">
          <h3 className="font-bold text-slate-900 flex items-center gap-2 mb-2"><IndianRupee className="h-5 w-5" /> {d.simplified ? "Fees" : "Lump-sum fees"}</h3>
          <div className="grid grid-cols-3 gap-2 text-center mb-3">
            <div className="rounded-xl bg-slate-50 p-3"><p className="text-[11px] text-slate-500">Agreed</p><p className="font-bold">{inr(d.lumpsum.agreed_total)}</p></div>
            <div className="rounded-xl bg-green-50 p-3"><p className="text-[11px] text-slate-500">Paid</p><p className="font-bold text-green-700">{inr(d.lumpsum.paid)}</p></div>
            <div className="rounded-xl bg-red-50 p-3"><p className="text-[11px] text-slate-500">Remaining</p><p className="font-bold text-red-600">{inr(d.lumpsum.remaining)}</p></div>
          </div>
          {d.collect_plan?.has_plan ? <PlanRows cp={d.collect_plan} /> : d.simplified ? (
            <p className="text-sm text-slate-500" data-testid="fees-simplified-note">Pay the outstanding balance any time — no fixed schedule set yet.</p>
          ) : (<>
            {d.lumpsum.installments.length > 0 ? (
              <div className="space-y-2">
                {d.lumpsum.installments.map((it, i) => (
                  <div key={i} className="rounded-xl border border-slate-200 bg-white p-3 flex items-center justify-between gap-2" data-testid={`fees-inst-${i}`}>
                    <div className="min-w-0"><p className="font-semibold text-slate-800">Instalment {it.no || i + 1}</p><p className="text-xs text-slate-500">Due {it.due_date ? formatDate(it.due_date) : "—"}</p></div>
                    <div className="flex items-center gap-2 sm:gap-3 shrink-0"><span className="font-bold">{inr(it.amount)}</span><Badge className={stBadge(it.status)}>{stLabel(it.status)}</Badge></div>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-slate-500">No instalment schedule set yet.</p>}
            {d.lumpsum.unscheduled_remaining > 0 && <p className="mt-2 text-xs text-amber-600">Unscheduled remaining balance: {inr(d.lumpsum.unscheduled_remaining)}</p>}
          </>)}
        </section>
      )}

      {d.plan_type && <ProposeSchedule d={d} onDone={load} />}

      <section data-testid="fees-history">
        <h3 className="font-bold text-slate-900 flex items-center gap-2 mb-2"><Receipt className="h-5 w-5" /> Payment history</h3>
        {d.history.length === 0 ? <p className="text-sm text-slate-500">No payments recorded yet.</p> : (
          <div className="space-y-2">
            {d.history.map((h) => (
              <div key={h.id} className="rounded-xl border border-slate-200 bg-white p-3 flex items-center justify-between gap-2 flex-wrap" data-testid={`fees-pay-${h.id}`}>
                <div className="min-w-0"><p className="font-semibold text-slate-800">{inr(h.amount)} · {(h.mode || "").toUpperCase()}</p><p className="text-xs text-slate-500 break-words">{formatDate(h.date)}{h.covered ? ` · ${h.covered}` : ""}{h.reference ? ` · Ref ${h.reference}` : ""}{h.receipt_no ? ` · ${h.receipt_no}` : ""}</p></div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge className={stBadge(h.status)}>{stLabel(h.status)}</Badge>
                  {h.can_download_receipt && <>
                    <Button size="sm" variant="outline" onClick={() => downloadReceiptPdf(h.id, "student", h.receipt_no)} data-testid={`fees-receipt-pdf-${h.id}`}><Download className="h-4 w-4 mr-1" /> PDF</Button>
                    <Button size="sm" variant="outline" onClick={() => emailReceipt(h.id, "student")} data-testid={`fees-receipt-email-${h.id}`}><Mail className="h-4 w-4 mr-1" /> Email</Button>
                  </>}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
